// AdminSettlementReplay.tsx — Phase 11G admin settlement-replay tool.
// Re-enqueues a settlement job for a finalized match. Requires re-typed
// password (X-Confirm-Token) before applying.
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look. Hard borders → soft
// arena-tinted insetting rings; neutral palette → arena/neon tokens.
// All state, props, and API calls are unchanged.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from '../../components/fieldStyles';
import ConfirmPasswordModal from '@baaligames/shared-ui/admin/components/ConfirmPasswordModal';

export default function ReplayPanel() {
  const [matchId, setMatchId] = useState('');
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canApply = matchId.trim().length > 0 && reason.trim().length > 0;

  async function doReplay(token: string) {
    setBusy(true); setErr(null);
    try {
      const r = await adminApi.post<{ match_id: string; re_enqueued: boolean; prior_snapshots: number }>(
        `/admin/settlement/replay/${encodeURIComponent(matchId.trim())}`,
        { reason: reason.trim(), force },
        token,
      );
      setToast(`Match ${r.match_id} re-enqueued. Prior snapshots: ${r.prior_snapshots}. Worker will process within ~5s.`);
      setMatchId(''); setReason(''); setForce(false);
      setConfirming(false);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      {/* Phase 7 — one Settlement area: hop to the queue/DLQ twin. */}
      <Link
        to="/?drill=dlq"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold
                   text-arena-600 dark:text-neon-200/70
                   bg-arena-100/70 dark:bg-white/[0.06]
                   ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                   hover:bg-arena-100 dark:hover:bg-white/10 transition"
      >
        <Icon name="swap_horiz" size={14} />
        <span>Also in Settlement: <span className="font-bold">stuck jobs (DLQ)</span></span>
      </Link>
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}

        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 space-y-4
                     bg-white dark:bg-arena-700/40
                     ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                     shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                     backdrop-blur"
        >
          <Field label="Match ID"
                 hint="Paste the match ID (e.g. ludo_1a2b3c4d). Must already exist in pending_settlements or DLQ.">
            <input value={matchId} onChange={(e) => setMatchId(e.target.value)}
                   placeholder="ludo_…"
                   className={inputCls + ' font-mono'} />
          </Field>

          <Field label="Reason (required — written to audit log)">
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Investigated under-settled match — corrected agent rate; re-running to refresh ledger."
                      className={inputCls + ' resize-y'} />
          </Field>

          <label className="flex items-start gap-3 cursor-pointer
                            rounded-xl px-3 py-2.5
                            bg-rose-50/70 dark:bg-rose-500/[0.08]
                            ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                   className="w-4 h-4 mt-0.5 accent-rose-600" />
            <span className="flex-1 text-[12.5px] text-rose-900 dark:text-rose-200">
              <b>Force replay</b> — proceed even if the match already has settlement snapshots.
              {' '}
              <span className="text-[11px] block text-rose-700 dark:text-rose-300/80 mt-0.5">
                Note: the worker still respects the unique <code>(match_id, agent_id, pillar)</code> idempotency
                index, so a force-replay against an already-settled match is a safe no-op. Use this when
                you've manually deleted snapshots and want a fresh pass.
              </span>
            </span>
          </label>

          <div className="flex justify-end">
            <button onClick={() => setConfirming(true)} disabled={!canApply || busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                               bg-amber-600 hover:bg-amber-500 active:bg-amber-700
                               text-white text-[12.5px] font-bold
                               shadow-[0_2px_10px_rgba(245,158,11,0.4)]
                               transition active:scale-95 disabled:opacity-50">
              <Icon name="restart_alt" size={13} /> Re-enqueue settlement
            </button>
          </div>
        </motion.div>

        <p className="text-[11px] text-arena-600 dark:text-neon-200/60 px-1 leading-relaxed">
          <Icon name="info" size={12} className="inline-block align-middle mr-1" />
          The settlement worker uses a <code>UNIQUE(match_id, agent_id, pillar)</code> index on
          {' '}<code>match_settlement_snapshots</code> for idempotency. A second pass on an already-settled
          match writes no new rows — making replay a safe operation. Force=true skips the
          "already has snapshots" guardrail and is mainly useful when you've manually patched ledger state.
        </p>
      </div>

      {confirming && (
        <ConfirmPasswordModal
          title={`Replay settlement for match ${matchId.trim()}?`}
          subtitle={`Reason: "${reason.trim()}". ${force ? 'Force=true (skips guard).' : ''} The worker will pick the job within ~5 seconds.`}
          dangerLabel="Confirm replay"
          tone="amber"
          onCancel={() => setConfirming(false)}
          onConfirmed={doReplay}
        />
      )}
    </div>
  );
}

// 2026-05-20 — aliased to canonical INPUT_CLS for dark-mode legibility.
const inputCls = INPUT_CLS;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 mb-1 font-semibold">{label}</span>
      {children}
      {hint && <p className="text-[10.5px] text-arena-600 dark:text-neon-200/60 mt-1">{hint}</p>}
    </label>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}
